import React, { useState, useEffect } from 'react';
import './UserList.css';
import apiClient from '../../api';
import { formatShortDate } from '../../utils/dateUtils';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/user/users/admin/');
      const data = response.data;

      // Handle both paginated response and direct array response
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (data && Array.isArray(data.results)) {
        setUsers(data.results);
      } else {
        setUsers([]);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/user/users/${userId}/admin/`);
      setUsers(users.filter(user => user.id !== userId));
      alert('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.split(' ');
    return words.length > 1
      ? (words[0][0] + words[1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredUsers = users.filter(user =>
    (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.phone_number && user.phone_number.includes(searchTerm))
  );

  if (loading) {
    return <div className="loading-message">Loading users...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="user-list">
      <div className="user-header">
        <h1 className="user-title">👥 User Management</h1>
        <div className="user-counter">
          <span className="counter-badge">
            <span className="counter-icon">👤</span>
            <span className="counter-number">{users.length}</span>
            <span className="counter-label">Total Users</span>
          </span>
          {searchTerm && filteredUsers.length !== users.length && (
            <span className="counter-badge filtered">
              <span className="counter-icon">🔍</span>
              <span className="counter-number">{filteredUsers.length}</span>
              <span className="counter-label">Found</span>
            </span>
          )}
        </div>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="🔍 Search users by name, username, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredUsers.length === 0 && users.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3 className="empty-title">No Users Found</h3>
          <p className="empty-description">
            No users have registered yet. Users will appear here once they sign up for the platform.
          </p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3 className="empty-title">No Matching Users Found</h3>
          <p className="empty-description">
            Try adjusting your search term to find users.
          </p>
        </div>
      ) : (
        <table className="user-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Username</th>
              <th>Email</th>
              <th className="hide-mobile">Phone</th>
              <th className="hide-mobile">Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="user-avatar">
                      {getInitials(user.first_name + ' ' + user.last_name)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {user.first_name} {user.last_name}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        ID: {user.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <strong>{user.username}</strong>
                </td>
                <td>{user.email || 'No email'}</td>
                <td className="hide-mobile">{user.phone_number || 'No phone'}</td>
                <td className="hide-mobile">{formatDate(user.date_joined)}</td>
                <td>
                  <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => alert(`User Details:\n\nName: ${user.first_name} ${user.last_name}\nUsername: ${user.username}\nEmail: ${user.email}\nPhone: ${user.phone_number || 'N/A'}\nJoined: ${formatDate(user.date_joined)}\nActive: ${user.is_active ? 'Yes' : 'No'}`)}
                      className="view-button"
                      title="View Details"
                    >
                      👁️ View
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="delete-button"
                      title="Delete User"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserList;